import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    Renderer2,
    afterNextRender,
    inject,
    input,
    model,
    output,
    viewChild,
} from '@angular/core';

import { TrackingEvents } from '../_shared/data/tracking-events.data';
import { LinkDirective } from '../_shared/directives/link.directive';
import { IconName } from '../_shared/models/icon.model';
import { ILink } from '../_shared/models/link.model';
import { ISubMenuItem } from '../_shared/models/navigation/menu/sub-menu-item.model';
import { GtmService } from '../_shared/services/gtm.service';
import { LinkService } from '../_shared/services/link.service';
import { DimmerComponent } from '../dimmer/dimmer.component';
import { IconComponent } from '../icon/icon.component';
import { SidebarItemComponent } from './sidebar-item/sidebar-item.component';

const DEFAULT_ITEM: ISubMenuItem = {
    submenuTitle: '',
    submenuItems: [],
};

@Component({
    selector: 'sirius-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(keydown.escape)': 'closeMenu()',
    },
    imports: [IconComponent, SidebarItemComponent, DimmerComponent, LinkDirective],
})
export class SidebarComponent {
    private readonly _document = inject<Document>(DOCUMENT);
    private readonly _renderer = inject(Renderer2);
    private readonly _gtmService = inject(GtmService);
    private readonly _linkService = inject(LinkService);

    readonly icon = input<IconName | undefined>(undefined);

    /**
     * `model()` so consumers can two-way bind and we can mutate it internally
     * as the user drills into / out of submenu levels without going through
     * the parent's change-detection.
     */
    readonly selectedItem = model<ISubMenuItem>(DEFAULT_ITEM);

    readonly menuClose = output<void>();
    readonly menuOpened = output<void>();

    private readonly _container = viewChild.required<ElementRef<HTMLElement>>('container');

    constructor() {
        this._renderer.setStyle(this._document.body, 'overflow', 'hidden');

        afterNextRender(() => {
            this._container().nativeElement.focus();
        });
    }

    next(index: number): void {
        const parent = this.selectedItem();
        const item = parent.submenuItems?.[index];

        if (!item || !item.submenuItems || !item.submenuItems.length) return;

        if (!item.submenuParent) {
            item.submenuParent = parent;
        }
        this.selectedItem.set(item);
    }

    previous(): void {
        const current = this.selectedItem();
        this.selectedItem.set(current.submenuParent ?? current);
    }

    closeMenu(): void {
        this._renderer.setStyle(this._document.body, 'overflow', 'auto');
        this.menuClose.emit();
    }

    isItemParent(item: ISubMenuItem): boolean {
        return !!item.submenuItems && item.submenuItems.length > 0;
    }

    onClick(e: Event, link?: ILink): void {
        this.addTracking(link, e.target as HTMLElement);

        if (!link) return;

        this.closeMenu();
    }

    onBlur(event: FocusEvent): void {
        const currentTarget = event.target as HTMLElement;
        const relatedTarget = event.relatedTarget as HTMLElement;

        if (!currentTarget.contains(relatedTarget)) {
            this.closeMenu();
        }
    }

    //#region tracking
    private addTracking(link: ILink | undefined, anchor: HTMLElement): void {
        const data = this._linkService.getTrackingData(anchor);
        const cfg = {
            ...link?.tracking?.parameters,
            link_classes: data.linkClasses,
            link_id: data.linkId,
            link_text: data.linkText,
            link_url: data.linkUrl,
            outbound: data.outbound,
        };

        this._gtmService.track(TrackingEvents.gtm['topMenuClick']([cfg]));
    }
    //#endregion
}
